import React, { useState, useEffect, useRef } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { useDispatch, useSelector } from 'react-redux';

import {
    Modal,
    Box,
    Typography,
    Button,
    Tooltip,
    Skeleton,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { CircularProgressbar, buildStyles } from 'react-circular-progressbar';
import 'react-circular-progressbar/dist/styles.css';

import { useTranslation } from 'react-i18next';
import { beautifyNumber } from '../../../Utils/FormatNumber.js';
import CustomMonthYearIndividualDatePicker from '../CustomDates/CustomMonthYearIndividualDatePicker/CustomMonthYearIndividualDatePicker.js';
import ReportsService from '../../../Services/reports.service.js';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
    setFinancialModalOpen,
    setFinancialModalLoading,
    setFinancialModalData,
    setFinancialModalDateRange
} from '../../../store/reducers/financialModalSlice.js';
dayjs.extend(utc);

const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 400,
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 4,
    borderRadius: '8px',
};

const FinancialModal = ({ client, startDate: propStartDate, endDate: propEndDate }) => {
    const { i18n, t } = useTranslation();
    const direction = i18n.dir();
    const dispatch = useDispatch();
    const { open, localLoading, startDate, endDate, data, lastFetchedStartDate, lastFetchedEndDate } =
        useSelector(state => state.financialModalSlice);

    // Local loading
    // const [localLoading, setLocalLoading] = useState(false);


    // Modal state
    const handleOpen = () => dispatch(setFinancialModalOpen(true));
    const handleClose = () => dispatch(setFinancialModalOpen(false));

    // Run-once ref to skip second mount in dev mode under React.StrictMode
    const didRunRef = useRef(false);
    const lastStartDateRef = useRef(propStartDate);
    const lastEndDateRef = useRef(propEndDate);
    const fetchInProgressRef = useRef(false);
    /**
     * Utility: fetch data using the current or passed start/end.
     */
    const fetchData = async (start, end) => {
        if (!client?.caseId || !client?.accountantId) return;
        if (fetchInProgressRef.current) return;
        fetchInProgressRef.current = true;
        dispatch(setFinancialModalLoading(true));

        try {
            const response = await ReportsService.calculateTax(
                client.caseId,
                client.accountantId,
                start.format('YYYY-MM-DD'),
                end.format('YYYY-MM-DD')
            );
            dispatch(setFinancialModalData(response));
        } catch (error) {
            console.error('Failed to fetch tax data:', error);
        } finally {
            fetchInProgressRef.current = false;
            dispatch(setFinancialModalLoading(false));
        }
    };

    /**
     * Effect #1: On mount (and if client changes), but only once (due to didRunRef).
     * This prevents the double call in dev mode under StrictMode.
     */

    useEffect(() => {
        const hasStartChanged = propStartDate !== lastStartDateRef.current;
        const hasEndChanged = propEndDate !== lastEndDateRef.current;

        if (didRunRef.current && !hasStartChanged && !hasEndChanged) return;
        didRunRef.current = true;

        // Set default dates to start and end of the year range from props
        let start, end;

        if (propStartDate && propEndDate) {
            // If both props are provided, extract the year and set to start/end of that year
            const year = dayjs.utc(propStartDate).year();
            start = dayjs.utc(`${year}-01-01`).startOf('year');
            end = dayjs.utc(`${year}-12-31`).endOf('year');

        } else if (propStartDate) {
            // If only start date is provided, use start of that year and end of current year
            const startYear = dayjs.utc(propStartDate).year();
            const currentYear = dayjs().year();
            start = dayjs.utc(startYear, 0, 1).startOf('year');
            end = dayjs.utc(currentYear, 11, 31).endOf('year');
        } else if (propEndDate) {
            // If only end date is provided, use start of current year and end of that year
            const endYear = dayjs.utc(propEndDate).year();
            const currentYear = dayjs().year();
            start = dayjs.utc(currentYear, 0, 1).startOf('year');
            end = dayjs.utc(endYear, 11, 31).endOf('year');
        } else {
            // If no props provided, use current year
            const currentYear = dayjs().year();
            start = dayjs.utc(currentYear, 0, 1).startOf('year');
            end = dayjs.utc(currentYear, 11, 31).endOf('year');
        }

        if (client?.caseId) {
            const alreadyFetched =
                start.isSame(lastFetchedStartDate, 'day') &&
                end.isSame(lastFetchedEndDate, 'day');

            if (alreadyFetched) return;
            dispatch(setFinancialModalDateRange({ startDate: start, endDate: end }));
            fetchData(start, end);
        }

        lastStartDateRef.current = propStartDate;
        lastEndDateRef.current = propEndDate;
    }, [client?.caseId, client?.accountantId, propStartDate, propEndDate]);



    /**
     * Effect #2: If propStartDate/propEndDate changes, sync local states and fetch once.
     */
    useEffect(() => {
        let newStart = startDate;
        let newEnd = endDate;
        let changed = false;

        if (propStartDate) {
            const propParsed = dayjs.utc(propStartDate);
            if (!propParsed.isSame(startDate, 'day')) {
                newStart = propParsed;
                changed = true;
            }
        }
        if (propEndDate) {
            const propParsedEnd = dayjs.utc(propEndDate).endOf('day');
            if (!propParsedEnd.isSame(endDate, 'day')) {
                newEnd = propParsedEnd;
                changed = true;
            }
        }

        // if (changed) {
        //   setStartDate(newStart);
        //   setEndDate(newEnd);
        //   if (client?.caseId && client?.accountantId) {
        //     fetchData(newStart, newEnd);
        //   }
        // }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [propStartDate, propEndDate]);

    /**
     * Date pickers: if user manually picks a new date, we update local states & fetch immediately.
     */
    const handleStartDateChangeNew = (newValue, type) => {
        const newDate = new Date(newValue);
        let updatedDate;
        if (type === 'year') {
            // When year changes, set to start of that year
            updatedDate = new Date(Date.UTC(newDate.getFullYear(), 0, 1));
        } else {
            // When month changes, keep the same year but change month to start of that month
            updatedDate = new Date(Date.UTC(startDate.toDate().getFullYear(), newDate.getMonth(), 1));
        }
        const dayjsDate = type === 'year' ? dayjs.utc(updatedDate).startOf('year') : dayjs.utc(updatedDate).startOf('month');
        dispatch(setFinancialModalDateRange({ startDate: dayjsDate, endDate }));
        fetchData(dayjsDate, endDate);
    };
    const handleEndDateChangeNew = (newValue, type) => {
        const newDate = new Date(newValue);
        let updatedDate;
        if (type === 'year') {
            // When year changes, set to end of that year
            updatedDate = new Date(Date.UTC(newDate.getFullYear(), 11, 31));
        } else {
            // When month changes, keep the same year but change month to end of that month
            updatedDate = new Date(Date.UTC(endDate.toDate().getFullYear(), newDate.getMonth() + 1, 0));
        }
        const dayjsDate = type === 'year' ? dayjs.utc(updatedDate).endOf('year') : dayjs.utc(updatedDate).endOf('month');
        dispatch(setFinancialModalDateRange({ startDate, endDate: dayjsDate }));
        fetchData(startDate, dayjsDate);
    };

    const existingAdvancePercentage = Math.max(0, client?.incomeTaxAdvancePercentage).toFixed(1);
    // Backend returns recommendedAdvancePercentage on 0–100 scale (same as client.IncomeTaxAdvancePercentage)
    const recommendedAdvancePctNum = Math.max(0, data?.recommendedAdvancePercentage ?? 0);
    const recommendedAdvancePercentage = recommendedAdvancePctNum.toFixed(1);
    const showWarning = existingAdvancePercentage !== recommendedAdvancePercentage;

    return (
        <div className={direction}>
            <Tooltip title={t('RecommendedAdvancePercentage')}>
                <div
                    className="DataSummary-card incomeExpense-Hover"
                    style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '50px',
                        padding: '10px 8px',
                        gap: '10px',
                        borderRadius: '8px',
                        border: '1px solid var(--Disabled, #BFBFC1)',
                        boxshadow: '0px 4px 12px rgba(0, 0, 0, 0.12), 0px 2px 4px rgba(156, 154, 154, 0.06)',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: '0 6px 20px rgba(48, 79, 255, 0.25)',
                        }
                    }}
                    onClick={handleOpen}
                >
                    {localLoading ? (
                        <>
                            {/* Same layout, but with Skeletons */}
                            <div style={{ display: 'flex', width: 30, height: 30, margin: '0 auto' }}>
                                <Skeleton variant="circular" width={30} height={30} />
                            </div>

                            <div
                                className="DataSummary-card-titleData-container"
                                style={{ textAlign: 'center', marginTop: 10 }}
                            >
                                <div className="DataSummary-card-title">
                                    <Skeleton variant="text" width={180} height={24} />
                                </div>

                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        color: '#ccc',
                                        marginTop: 2,
                                        justifyContent: 'center',
                                        gap: 8,
                                    }}
                                >
                                    <Skeleton variant="circular" width={20} height={20} />
                                    <Skeleton variant="text" width={100} height={20} />
                                </div>
                            </div>
                        </>
                    ) : (
                        <>
                            <div
                                className="DataSummary-card-titleData-container"
                                style={{
                                    textAlign: 'center',
                                    marginTop: '8px',
                                    display: 'flex',
                                    flexDirection: 'row',
                                    gap: '8px',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    width: '100%',
                                    maxWidth: '240px',
                                }}
                            >
                                <div style={{ width: 50, height: 50, fontWeight: 'bold' }}>
                                    <CircularProgressbar
                                        value={Math.min(100, recommendedAdvancePctNum)}
                                        text={`${recommendedAdvancePercentage}%`}
                                        styles={buildStyles({
                                            textColor: '#304FFF',
                                            textSize: '24px',
                                            fontWeight: 'bold',

                                        })}
                                    />
                                </div>
                                <div
                                    className="DataSummary-card-title"
                                    style={{
                                        color: '#304FFF',
                                        fontSize: '12px',
                                        fontWeight: '600',
                                        lineHeight: '1.2',
                                        textAlign: 'center'
                                    }}
                                >
                                    {t('RecommendedAdvancePercentage')}
                                </div>


                            </div>
                        </>
                    )}
                </div>
            </Tooltip>

            {/* Modal */}
            <Modal open={open} onClose={handleClose}>
                <Box sx={modalStyle} className={direction}>
                    {showWarning && (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                mb: 2,
                                color: 'error.main',
                            }}
                        >
                            <WarningAmberIcon />
                            <Typography variant="body2" color="error">
                                {t('WarningValuesMismatchBetweenRecommendedAndExistingPercentage')}
                            </Typography>
                        </Box>
                    )}

                    {/* Start Date */}
                    <Typography variant="body1" gutterBottom>
                        {t('StartDate')}
                    </Typography>
                    <CustomMonthYearIndividualDatePicker
                        year={startDate}
                        month={startDate}
                        onYearChange={(newYear) => handleStartDateChangeNew(newYear, 'year')}
                        onMonthChange={(newMonth) => handleStartDateChangeNew(newMonth, 'month')}
                        labelYear={t('StartYear')}
                        labelMonth={t('StartMonth')}
                        height="48px"
                        widthOuter="270px"
                        widthInner="120px"
                        flip={true}
                    />

                    {/* End Date */}
                    <Typography variant="body1" gutterBottom mt={2}>
                        {t('EndDate')}
                    </Typography>
                    <CustomMonthYearIndividualDatePicker
                        year={endDate}
                        month={endDate}
                        onYearChange={(newYear) => handleEndDateChangeNew(newYear, 'year')}
                        onMonthChange={(newMonth) => handleEndDateChangeNew(newMonth, 'month')}
                        labelYear={t('EndYear')}
                        labelMonth={t('EndMonth')}
                        height="48px"
                        widthOuter="270px"
                        widthInner="120px"
                        flip={true}
                    />

                    {/* Existing vs. Recommended Circulars */}
                    <div style={{ display: 'flex', justifyContent: 'center', gap: 20, margin: '20px auto' }}>
                        <div style={{ width: 150, height: 150 }}>
                            <CircularProgressbar
                                value={Math.max(0, client?.incomeTaxAdvancePercentage)}
                                text={`${Math.max(0, client?.incomeTaxAdvancePercentage).toFixed(1)}%`}
                                styles={buildStyles({
                                    pathColor: '#4caf50',
                                    textColor: '#333',
                                })}
                            />
                            <Typography variant="body1" textAlign="center" mt={2}>
                                {t('ExistingAdvancePercentage')}
                            </Typography>
                        </div>
                        <div style={{ width: 150, height: 150 }}>
                            <CircularProgressbar
                                value={Math.min(100, recommendedAdvancePctNum)}
                                text={`${recommendedAdvancePercentage}%`}
                                styles={buildStyles({
                                    pathColor: '#4caf50',
                                    textColor: '#333',
                                })}
                            />
                            <Typography variant="body1" textAlign="center" mt={2}>
                                {t('RecommendedAdvancePercentage')}
                            </Typography>
                        </div>
                    </div>

                    {/* Financial data boxes */}
                    <Box mt={4} sx={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: 10 }}>
                        {/* Profit */}
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                backgroundColor: '#f5f5f5',
                                padding: '16px',
                                borderRadius: '8px',
                                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                            }}
                        >
                            <Typography variant="h6" color="primary">
                                {t('MonthlyFinancialProfit')}:
                            </Typography>
                            <Typography variant="h5" color="text.secondary" fontWeight="bold">
                                {beautifyNumber(data.profit)}
                            </Typography>
                        </Box>

                        {/* Income */}
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                backgroundColor: '#f5f5f5',
                                padding: '16px',
                                borderRadius: '8px',
                                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                            }}
                        >
                            <Typography variant="h6" color="primary">
                                {t('MonthlyIncome')}:
                            </Typography>
                            <Typography variant="h5" color="text.secondary" fontWeight="bold">
                                {beautifyNumber(data.incomes)}
                            </Typography>
                        </Box>

                        {/* Estimated Tax */}
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                backgroundColor: '#f5f5f5',
                                padding: '16px',
                                borderRadius: '8px',
                                boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                            }}
                        >
                            <Typography variant="h6" color="primary">
                                {t('EstimatedMonthlyTax')}:
                            </Typography>
                            <Typography variant="h5" color="text.secondary" fontWeight="bold">
                                {beautifyNumber(data.estimatedTax)}
                            </Typography>
                        </Box>
                    </Box>

                    {/* Close Button */}
                    <Box mt={4} textAlign="center">
                        <Button variant="outlined" onClick={handleClose}>
                            {t('Close')}
                        </Button>
                    </Box>
                </Box>
            </Modal>
        </div>
    );
};

export default FinancialModal;
